import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import RankBadge from "../components/RankBadge";
import { ArrowRight, Check } from "lucide-react";

/**
 * Welcome interstitial shown immediately after a successful Stripe checkout.
 * Goal: turn the dead time between Stripe redirect and dashboard into a moment
 * of brand reinforcement + rank-system anticipation. Auto-advances after 8s
 * or on CTA click. If the user lands here without being premium yet (rare
 * edge case where Stripe is still propagating), we sync once and otherwise
 * still let them in — they're authenticated.
 */
const TAGLINES = [
    "Mechanical tension. No fluff.",
    "Heavy reps. Real progress.",
    "Show up. Add weight. Repeat.",
    "No junk volume. No noise.",
];

const PILLARS = [
    "Two splits — Upper/Lower or Full Body",
    "4–8 reps · 1–2 RIR · double progression",
    "Built-in exercise library + form guides",
    "Track rank, EXP, PRs, body imbalances",
];

// Phrases the dynamic headline cycles through. Each one finishes the sentence
// "It's time to ___" — short, punchy, increasing in intensity. Chosen so the
// reader is reading a new identity statement every couple of seconds.
const ROTATING_VERBS = [
    "train with purpose.",
    "stop guessing.",
    "build real muscle.",
    "earn every rep.",
    "show up.",
    "outwork yesterday.",
];

/**
 * Typewriter-cycling phrase. Types out the current phrase, holds for ~1.4s,
 * then deletes back to empty and advances to the next. Pure setTimeout —
 * no external animation library. Pauses when the tab is hidden so the
 * effect doesn't waste cycles in the background.
 */
function TypewriterRotator({ phrases, typeMs = 55, deleteMs = 30, holdMs = 1400 }) {
    const [idx, setIdx] = useState(0);
    const [text, setText] = useState("");
    const [phase, setPhase] = useState("typing"); // typing | holding | deleting

    useEffect(() => {
        const current = phrases[idx % phrases.length];
        let t;
        if (phase === "typing") {
            if (text.length < current.length) {
                t = setTimeout(() => setText(current.slice(0, text.length + 1)), typeMs);
            } else {
                t = setTimeout(() => setPhase("holding"), holdMs);
            }
        } else if (phase === "holding") {
            t = setTimeout(() => setPhase("deleting"), 0);
        } else if (phase === "deleting") {
            if (text.length > 0) {
                t = setTimeout(() => setText(text.slice(0, -1)), deleteMs);
            } else {
                setPhase("typing");
                setIdx((i) => (i + 1) % phrases.length);
            }
        }
        return () => clearTimeout(t);
    }, [text, phase, idx, phrases, typeMs, deleteMs, holdMs]);

    return (
        <span data-testid="welcome-rotator" className="inline-block">
            <span className="text-accent-red">{text}</span>
            <span className="ml-0.5 inline-block w-[0.08em] animate-pulse text-accent-red" aria-hidden>
                |
            </span>
        </span>
    );
}

export default function Welcome() {
    const { user, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [rank, setRank] = useState(null);
    const [tagline] = useState(() => TAGLINES[Math.floor(Math.random() * TAGLINES.length)]);
    const [autoSeconds, setAutoSeconds] = useState(8);

    useEffect(() => {
        // Pull the user's starting rank/EXP. If they're brand new this is rank 1.
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/progress/rank");
                if (!cancelled) setRank(data);
            } catch (_) { /* non-blocking */ }
        })();
        // Re-sync in case the user landed here a hair before Stripe's webhook arrived
        (async () => {
            try { await api.post("/payments/sync"); await checkAuth(); } catch (_) {}
        })();
        return () => { cancelled = true; };
    }, [checkAuth]);

    useEffect(() => {
        if (autoSeconds <= 0) {
            navigate("/dashboard");
            return;
        }
        const t = setTimeout(() => setAutoSeconds(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [autoSeconds, navigate]);

    const enter = () => navigate("/dashboard");

    const rankId = rank?.current?.id || 1;
    const rankName = rank?.current?.name || "Newcomer";
    const rankTaglineLine = rank?.current?.tagline || "you've got work to do";
    const totalExp = rank?.total_exp ?? 0;
    const nextRank = rank?.next?.name;
    const toNext = rank?.to_next;

    return (
        <div
            className="relative min-h-screen overflow-hidden bg-background"
            data-testid="welcome-page"
            style={{ backgroundColor: "#0A0A0A" }}
        >
            {/* radial accent glow */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(255,59,48,0.18) 0%, rgba(255,59,48,0) 60%), radial-gradient(ellipse 60% 50% at 50% 100%, rgba(255,59,48,0.10) 0%, rgba(255,59,48,0) 60%)",
                }}
            />
            {/* film grain — subtle */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
            />

            <div className="relative mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6 py-16 text-center text-white">
                {/* check pill */}
                <div
                    className="mb-6 inline-flex items-center gap-2 border border-accent-red bg-accent-red/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-accent-red"
                    data-testid="welcome-confirm-pill"
                >
                    <Check className="h-3 w-3" />
                    <span>Payment confirmed</span>
                </div>

                {/* eyebrow */}
                <div className="label-eyebrow text-accent-red">Welcome aboard, {user?.name?.split(" ")[0] || "lifter"}</div>

                {/* big H1 with the user's first name baked in */}
                <h1
                    className="mt-3 font-display text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-7xl"
                    style={{ textShadow: "0 2px 24px rgba(255,59,48,0.18)" }}
                    data-testid="welcome-heading"
                >
                    You&apos;re in, <span className="text-accent-red">{user?.name?.split(" ")[0] || "lifter"}</span>.
                </h1>

                {/* Dynamic rotating sub-headline — keeps the eye moving and
                    reinforces identity statements (Cialdini: consistency). */}
                <p className="mt-5 max-w-xl text-base font-medium uppercase tracking-[0.14em] text-white/85 sm:text-xl" data-testid="welcome-dynamic-line">
                    It&apos;s time to{" "}
                    <TypewriterRotator phrases={ROTATING_VERBS} />
                </p>

                {/* selected static tagline */}
                <p className="mt-3 max-w-xl text-xs uppercase tracking-[0.18em] text-white/55">
                    {tagline}
                </p>

                {/* rank reveal — your starting point */}
                <div
                    className="mt-10 flex flex-col items-center gap-4 border border-white/10 bg-white/[0.02] p-8 backdrop-blur-sm sm:flex-row sm:gap-8 sm:p-10"
                    data-testid="welcome-rank-reveal"
                >
                    <div className="flex-none">
                        <RankBadge rankId={rankId} size="lg" />
                    </div>
                    <div className="text-left">
                        <div className="label-eyebrow text-white/50">Your starting rank</div>
                        <div className="mt-1 font-display text-3xl font-black uppercase text-white sm:text-4xl">
                            {rankName}
                        </div>
                        <p className="mt-1 max-w-xs text-sm italic text-white/65">"{rankTaglineLine}"</p>
                        <div className="mt-3 text-[11px] uppercase tracking-widest text-white/40">
                            {totalExp.toLocaleString()} EXP
                            {nextRank && toNext != null && (
                                <span className="ml-2 text-white/60">· {toNext.toLocaleString()} to {nextRank}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* pillars */}
                <ul className="mt-10 grid w-full max-w-xl grid-cols-1 gap-2 text-left text-sm text-white/80 sm:grid-cols-2" data-testid="welcome-pillars">
                    {PILLARS.map((p) => (
                        <li key={p} className="flex items-start gap-2">
                            <Check className="mt-0.5 h-4 w-4 flex-none text-accent-red" />
                            <span>{p}</span>
                        </li>
                    ))}
                </ul>

                {/* CTA */}
                <button
                    onClick={enter}
                    data-testid="welcome-enter-btn"
                    className="mt-10 inline-flex items-center gap-2 rounded-sm bg-accent-red px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_40px_-12px_rgba(255,59,48,0.7)] transition hover:bg-[color:var(--accent-hover)] hover:shadow-[0_0_60px_-8px_rgba(255,59,48,0.9)]"
                >
                    Enter the app
                    <ArrowRight className="h-4 w-4" />
                </button>

                <div className="mt-3 text-[10px] uppercase tracking-widest text-white/30" data-testid="welcome-auto-counter">
                    Auto-redirect in {autoSeconds}s
                </div>
            </div>
        </div>
    );
}
